"use client";

import { c as _c } from "react/compiler-runtime";
import { Plus, Sliders, SpinnerGap, UploadSimple } from "@phosphor-icons/react";
import { useCallback, useState } from "react";
import { CARD_IMAGE_ASPECT, type CardImageAspect } from "@/components/storefront/card-image-aspect";
import { Price } from "@/components/storefront/Price";
import { PriceLabel } from "@/components/storefront/PriceLabel";
import { CrossfadeImage } from "@/components/storefront/CrossfadeImage";
import { SafeImage } from "@/components/storefront/SafeImage";
import StockBadge from "@/components/storefront/StockBadge";
import { useQuickView } from "@/components/storefront/structured-widgets/ecommerce/QuickViewProvider";
import { toast } from "@/components/storefront/ui/toast";
import { Link } from "@/i18n/navigation";
import { capturePostHogEvent } from "@/lib/analytics/posthog-capture";
import { useCart } from "@/lib/cart/CartContext";
import { CartStockError } from "@/lib/cart/stock";
import { useStockDisplay } from "@/lib/storefront/stock-display";
import { defaultProductUnit, unitPrice as toUnitPrice, unitMinBase } from "@/lib/units/convert";
import type { ProductUnitDTO, UnitDTO } from "@/lib/units/types";
export type { CardImageAspect };
export type VariantColorSwatch = {
  name: string;
  hex?: string | null;
  image_url?: string | null;
};
export type ProductForCard = {
  id: string;
  slug: string;
  name_ar: string;
  name_en: string;
  description_ar?: string | null;
  description_en?: string | null;
  price: number;
  coverImageUrl: string | null;
  /** Cover image LQIP for placeholder="blur". Null pre-backfill rows. */
  coverImageBlur?: string | null;
  hoverImageUrl?: string | null;
  /** Spec rows, used by the glass-spec listing card for its chips. */
  specs?: Array<{
    key_ar?: string;
    key_en?: string;
    value_ar?: string;
    value_en?: string;
  }>;
  created_at?: string;
  min_order_qty?: number;
  /** Increment the quantity stepper moves by — products sold in packs. */
  qty_step?: number;
  product_type?: string;
  track_stock?: boolean;
  stock?: number | null;
  /**
   * Cheapest / dearest sellable variant. Populated for variable products only;
   * drives the "starting from" line when the variants span a range.
   */
  min_variant_price?: number | null;
  max_variant_price?: number | null;
  variant_colors?: VariantColorSwatch[];
  variant_sizes?: string[];
  sale_price?: number | null;
  original_price?: number | null;
  /** The unit `price` and `stock` are counted in. Absent for a product without units. */
  base_unit?: UnitDTO | null;
  /** Units the customer may buy in. Empty/absent for a product without units. */
  units?: ProductUnitDTO[];
};

/**
 * Card call to action shape. `icon` is the circular control floating over the
 * image; `inline` is a full-width labelled button under the price. Listing
 * pages opt into `inline`; carousels and widgets keep the compact circle.
 */
export type CardAction = "icon" | "inline";
const ICON_ACTION_CLASS = "tap-target h-9 w-9 flex items-center justify-center rounded-full bg-card/90 backdrop-blur-sm shadow-sm hover:bg-primary hover:text-primary-foreground active:scale-[0.95] transition-all duration-200";
const INLINE_ACTION_CLASS = "w-full h-10 px-3 flex items-center justify-center gap-1.5 rounded-xl border border-border bg-card text-sm font-medium leading-none text-foreground whitespace-nowrap hover:bg-primary hover:text-primary-foreground hover:border-primary active:scale-[0.98] transition-all duration-200";
const ACTION_CLASS: Record<CardAction, string> = {
  icon: ICON_ACTION_CLASS,
  inline: INLINE_ACTION_CLASS
};
export function CardAddToCartButton({
  productId,
  price,
  minQty,
  stock,
  locale,
  action = "icon",
  productUnitId = null
}: {
  productId: string;
  price: number;
  minQty: number;
  /** Effective available units (null/undefined = untracked / unlimited). */
  stock?: number | null;
  locale: string;
  action?: CardAction;
  /** The card's default selling unit, when the product has units. */
  productUnitId?: string | null;
}) {
  const {
    addItem
  } = useCart();
  const [loading, setLoading] = useState(false);
  const label = locale === "ar" ? "إضافة إلى السلة" : "Add to Cart";
  async function handleAddToCart(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    setLoading(true);
    try {
      await addItem(productId, minQty, minQty, null, stock, null, productUnitId);
      capturePostHogEvent("add_to_cart", {
        product_id: productId,
        price,
        quantity: minQty
      });
      toast.success(locale === "ar" ? "تمت الإضافة للسلة" : "Added to cart");
    } catch (err) {
      if (err instanceof CartStockError) {
        toast.danger(locale === "ar" ? `الكمية المتاحة في المخزون ${err.available} فقط` : `Only ${err.available} in stock`);
      } else {
        toast.danger(locale === "ar" ? "تعذّر إضافة المنتج. حاول مرة أخرى." : "Could not add product. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }
  return <button type="button" onClick={handleAddToCart} disabled={loading} aria-busy={loading || undefined} title={label} aria-label={label} className={`${ACTION_CLASS[action]} disabled:opacity-60 disabled:cursor-not-allowed`}>
      {loading ? <SpinnerGap size={16} className="animate-spin" /> : action === "inline" ? <span className="truncate">{label}</span> : <Plus size={16} weight="bold" />}
    </button>;
}

/**
 * Variable products open the quick view rather than adding directly: stock and
 * price are per-variant, so there is nothing meaningful to add until the
 * shopper has chosen. Falls back to the product page when no quick view
 * provider is mounted (the admin live preview), so the control is never dead.
 */
function ChooseOptionsButton(t0) {
  const $ = _c(16);
  const {
    slug,
    locale,
    action: t1
  } = t0;
  const action = t1 === undefined ? "icon" : t1;
  const {
    openQuickView,
    enabled
  } = useQuickView();
  const label = locale === "ar" ? "\u0639\u0631\u0636 \u0627\u0644\u062E\u064A\u0627\u0631\u0627\u062A" : "See options";
  const className = ACTION_CLASS[action];
  let t2;
  if ($[0] !== action || $[1] !== label) {
    t2 = action === "inline" ? <span className="truncate">{label}</span> : <Sliders size={16} weight="bold" />;
    $[0] = action;
    $[1] = label;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const content = t2;
  if (!enabled) {
    const t3 = `/products/${slug}`;
    let t4;
    if ($[3] !== className || $[4] !== content || $[5] !== label || $[6] !== t3) {
      t4 = <Link href={t3} title={label} aria-label={label} className={className}>{content}</Link>;
      $[3] = className;
      $[4] = content;
      $[5] = label;
      $[6] = t3;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  let t3;
  if ($[8] !== openQuickView || $[9] !== slug) {
    t3 = e => {
      e.preventDefault();
      e.stopPropagation();
      openQuickView(slug);
    };
    $[8] = openQuickView;
    $[9] = slug;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== className || $[12] !== content || $[13] !== label || $[14] !== t3) {
    t4 = <button type="button" onClick={t3} title={label} aria-label={label} className={className}>{content}</button>;
    $[11] = className;
    $[12] = content;
    $[13] = label;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}

/**
 * Sold-out placeholder for the inline row. The inline CTA is the last element
 * of every card, so omitting it on a sold-out product would leave that card
 * shorter than its neighbours and break the row's baseline.
 */
function SoldOutButton(t0) {
  const $ = _c(2);
  const {
    locale
  } = t0;
  const t1 = locale === "ar" ? "\u0646\u0641\u062F \u0627\u0644\u0645\u062E\u0632\u0648\u0646" : "Out of stock";
  let t2;
  if ($[0] !== t1) {
    t2 = <button type="button" disabled={true} className={`${INLINE_ACTION_CLASS} cursor-not-allowed opacity-60 hover:bg-card hover:text-foreground hover:border-border`}><span className="truncate">{t1}</span></button>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
}
export function ShareButton(t0) {
  const $ = _c(10);
  const {
    slug,
    name,
    locale,
    size: t1
  } = t0;
  const size = t1 === undefined ? "card" : t1;
  let t2;
  if ($[0] !== locale || $[1] !== name || $[2] !== slug) {
    t2 = async function handleShare(e) {
      e.preventDefault();
      e.stopPropagation();
      const url = `${window.location.origin}/${locale}/products/${slug}`;
      if (navigator.share) {
        try {
          await navigator.share({
            title: name,
            url
          });
        } catch {}
      } else {
        await navigator.clipboard.writeText(url);
        toast.success(locale === "ar" ? "\u062A\u0645 \u0646\u0633\u062E \u0627\u0644\u0631\u0627\u0628\u0637" : "Link copied");
      }
    };
    $[0] = locale;
    $[1] = name;
    $[2] = slug;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleShare = t2;
  const t3 = locale === "ar" ? "\u0645\u0634\u0627\u0631\u0643\u0629" : "Share";
  const t4 = locale === "ar" ? "\u0645\u0634\u0627\u0631\u0643\u0629" : "Share";
  const t5 = size === "card" ? "tap-target h-9 w-9 flex items-center justify-center rounded-full bg-card/90 backdrop-blur-sm shadow-sm hover:bg-primary hover:text-primary-foreground active:scale-[0.95] transition-all duration-200" : "h-9 w-9 flex items-center justify-center rounded-full bg-subtle/60 hover:bg-primary hover:text-primary-foreground active:scale-[0.95] transition-all duration-200";
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <UploadSimple size={14} />;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== handleShare || $[6] !== t3 || $[7] !== t4 || $[8] !== t5) {
    t7 = <button type="button" onClick={handleShare} title={t3} aria-label={t4} className={t5}>{t6}</button>;
    $[5] = handleShare;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  return t7;
}
export default function ProductCard({
  product,
  locale,
  priority = false,
  variant = "default",
  imageAspect = "portrait",
  action = "icon"
}: {
  product: ProductForCard;
  locale: string;
  priority?: boolean;
  variant?: "default" | "compact" | "horizontal";
  /** Grid card only — compact and horizontal use fixed thumbnail sizes. */
  imageAspect?: CardImageAspect;
  /**
   * Grid card only. `inline` moves the buy control out of the image overlay
   * and into a labelled full-width button beneath the price.
   */
  action?: CardAction;
}) {
  const stockDisplay = useStockDisplay();
  const name = locale === "ar" ? product.name_ar : product.name_en;
  const isNew = product.created_at ? Date.now() - new Date(product.created_at).getTime() < 7 * 24 * 60 * 60 * 1000 : false;
  const isVariable = product.product_type === "variable";
  const isOutOfStock = product.track_stock && product.stock !== null && product.stock !== undefined && product.stock <= 0 && product.product_type === "simple";
  // Stock messaging is suppressed entirely when the store hides stock display.
  const showStockBadge = stockDisplay !== "hide";
  // Card-level availability chip. Only simple products carry reliable card-level
  // stock here (variable stock is per-variant, resolved on the PDP/quick view),
  // so non-simple/untracked products pass trackStock=false and StockBadge no-ops.
  const cardTracksStock = product.product_type === "simple" && !!product.track_stock;
  const cardStock = cardTracksStock ? product.stock ?? null : null;
  const colors = product.variant_colors ?? [];
  const sizes = product.variant_sizes ?? [];

  // A variable product whose variants span a range showed the cheapest one as
  // a flat price, reading as *the* price. Lead with "starting from" instead,
  // and only when the variants genuinely differ — a uniform set is a definite
  // price and should not be hedged.
  const variantMin = product.min_variant_price ?? null;
  const variantMax = product.max_variant_price ?? null;
  const startingFrom = isVariable && variantMin != null && variantMax != null && variantMax > variantMin;
  const displayBase = startingFrom ? variantMin : product.price;

  // The default selling unit, when the product has any. A product with no
  // units (the common case) keeps defaultUnit null and every price/add-to-cart
  // path below renders exactly as it did before this feature existed.
  const defaultUnit = defaultProductUnit(product.units ?? []);
  const cardMinQty = defaultUnit ? unitMinBase(product.min_order_qty ?? 1, defaultUnit.factor) : product.min_order_qty ?? 1;
  const hasSale = product.sale_price != null && product.original_price != null;
  // The unit-price headline is derived from `displayBase`, which is the LIST
  // price (or the variant "starting from" price) — never the sale price. A
  // product on sale must keep showing the normal <Price> block (struck-through
  // original + discounted current), same as `structured-widgets/ProductCard`'s
  // `renderPrice` gate, rather than quoting a per-unit price computed off a
  // number the customer isn't actually charged. A variable product is
  // excluded too: `displayBase` there is a range's low end, not a single
  // sellable price to multiply by the unit factor.
  const showUnitPricing = defaultUnit !== null && !isVariable && !hasSale;

  // Horizontal variant: row layout with fixed thumbnail
  if (variant === "horizontal") {
    return <Link href={`/products/${product.slug}`} className="flex flex-row gap-3 group">
        <div className="relative w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-subtle">
          <SafeImage src={product.coverImageUrl} alt={name} fill className="object-cover transition-all duration-300 group-hover:scale-105" sizes="96px" priority={priority} blurDataURL={product.coverImageBlur} fallback={<div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                {locale === "ar" ? "لا توجد صورة" : "No image"}
              </div>} />
        </div>
        <div className="flex flex-col justify-center gap-1 min-w-0">
          <p data-card-name="" className="line-clamp-1 text-sm font-medium leading-snug text-foreground group-hover:underline">
            <bdi dir="auto">{name}</bdi>
          </p>
          <Price pricing={{
          base: product.price
        }} locale={locale} size="sm" />
          {showStockBadge && <StockBadge stock={cardStock} trackStock={cardTracksStock} />}
        </div>
      </Link>;
  }

  // Compact variant: smaller card with reduced image height
  if (variant === "compact") {
    return <div className="group relative flex flex-col">
        <div className="relative h-36 overflow-hidden rounded-xl bg-subtle">
          <Link href={`/products/${product.slug}`} className="block w-full h-full">
            <SafeImage src={product.coverImageUrl} alt={name} fill className="object-cover transition-all duration-300 group-hover:scale-105" sizes="(max-width: 768px) 50vw, 25vw" priority={priority} blurDataURL={product.coverImageBlur} fallback={<div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                  {locale === "ar" ? "لا توجد صورة" : "No image"}
                </div>} />
          </Link>
          {isNew && <span className="absolute start-2 top-2 rounded-full bg-primary px-2 py-0.5 text-[10px] font-medium text-primary-foreground shadow-sm">
              {locale === "ar" ? "جديد" : "New"}
            </span>}
        </div>
        <div className="mt-2 p-0.5">
          <Link href={`/products/${product.slug}`} className="hover:underline">
            <p data-card-name="" className="line-clamp-1 text-xs font-medium leading-snug text-foreground">
              <bdi dir="auto">{name}</bdi>
            </p>
          </Link>
          <Price pricing={{
          base: product.price
        }} locale={locale} size="sm" className="mt-1" />
          {showStockBadge && <div className="mt-1">
              <StockBadge stock={cardStock} trackStock={cardTracksStock} />
            </div>}
        </div>
      </div>;
  }

  // Active image — swaps when clicking a color swatch
  const [activeImage, setActiveImage] = useState(product.coverImageUrl);
  const [activeColorIdx, setActiveColorIdx] = useState<number | null>(null);
  const handleColorClick = useCallback((e: React.MouseEvent, color: VariantColorSwatch, idx: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (color.image_url) {
      setActiveImage(color.image_url);
    }
    setActiveColorIdx(idx);
  }, []);
  return <div className="group relative flex h-full flex-col">
      {/* Image container */}
      <div className={`relative ${CARD_IMAGE_ASPECT[imageAspect]} overflow-hidden rounded-2xl bg-subtle`}>
        <Link href={`/products/${product.slug}`} className="block w-full h-full">
          <CrossfadeImage src={activeImage} alt={name} fill className={`object-cover transition-all duration-300 ${product.hoverImageUrl ? "group-hover:opacity-0" : "group-hover:scale-105"}`} sizes="(max-width: 768px) 50vw, 25vw" priority={priority} loading={priority ? "eager" : "lazy"} fetchPriority={priority ? "high" : "auto"} blurSrc={product.coverImageUrl} blurDataURL={product.coverImageBlur} fallback={<div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
                {locale === "ar" ? "لا توجد صورة" : "No image"}
              </div>} />
          {activeImage && product.hoverImageUrl && <SafeImage src={product.hoverImageUrl} alt={name} fill className="object-cover opacity-0 transition-opacity duration-300 group-hover:opacity-100" sizes="(max-width: 768px) 50vw, 25vw" loading="lazy" />}
        </Link>

        {/* Badge — top start */}
        <div className="absolute start-2.5 top-2.5 flex flex-col gap-1">
          {isNew && <span className="rounded-full bg-primary px-2.5 py-0.5 text-xs font-medium text-primary-foreground shadow-sm">
              {locale === "ar" ? "جديد" : "New"}
            </span>}
          {showStockBadge && <StockBadge stock={cardStock} trackStock={cardTracksStock} />}
        </div>

        {/* Share button — top end */}
        <div className="absolute top-2.5 end-2.5">
          <ShareButton slug={product.slug} name={name} locale={locale} />
        </div>

        {/* Add to cart — bottom end. A variable product cannot be added from
            the card (its options are unchosen), so it opens the quick view
            instead of showing nothing at all: the card previously had no CTA
            whatsoever, which read as an unbuyable product. */}
        {action === "icon" && !isOutOfStock && <div className="absolute bottom-2.5 end-2.5">
            {isVariable ? <ChooseOptionsButton slug={product.slug} locale={locale} /> : <CardAddToCartButton productId={product.id} price={product.price} minQty={cardMinQty} stock={product.track_stock && product.stock != null ? product.stock : null} locale={locale} productUnitId={defaultUnit?.id ?? null} />}
          </div>}
      </div>

      {/* Text content
          Grows to fill the row so the price can be pinned to the bottom. Cards
          in a row are only as tall as the tallest, and a variant card carries a
          swatch strip a plain card does not — without this the price floats at
          a different height in every column and the grid reads ragged. */}
      <div className="mt-3 flex flex-1 flex-col px-0.5">
        <Link href={`/products/${product.slug}`} className="hover:underline">
          <p data-card-name="" className="line-clamp-1 text-sm font-medium leading-snug text-foreground">
            <bdi dir="auto">{name}</bdi>
          </p>
        </Link>

        {/* Color swatches — clickable to swap image */}
        {isVariable && colors.length > 0 && <div className="flex flex-nowrap items-center gap-1.5 mt-2 overflow-hidden">
            {colors.slice(0, 7).map((color_0, i) => {
          const isActive = activeColorIdx === i;
          // Image-thumbnail swatch (Nike-style) when the variant has an
          // assigned image. Falls back to a hex dot, then a name pill.
          if (color_0.image_url) {
            return <button key={color_0.name} type="button" title={color_0.name} aria-label={color_0.name} onClick={e_0 => handleColorClick(e_0, color_0, i)} className={["relative h-8 w-8 overflow-hidden rounded-lg border-2 shrink-0 transition-all duration-150 hover:scale-105", isActive ? "border-foreground ring-1 ring-foreground/20" : "border-border"].join(" ")}>
                    <SafeImage src={color_0.image_url} alt={color_0.name} fill className="object-cover" sizes="40px" loading="lazy" />
                  </button>;
          }
          if (color_0.hex) {
            return <button key={color_0.name} type="button" title={color_0.name} onClick={e_1 => handleColorClick(e_1, color_0, i)} className={["tap-target-24 w-5 h-5 rounded-full border-2 shrink-0 transition-all duration-150 hover:scale-110", isActive ? "border-foreground ring-1 ring-foreground/20" : "border-border"].join(" ")} style={{
              backgroundColor: color_0.hex
            }} />;
          }
          // No hex → text pill with the full name. Falls back gracefully
          // when the catalog stores color names without a paired hex.
          return <button key={color_0.name} type="button" onClick={e_2 => handleColorClick(e_2, color_0, i)} className={["tap-target-24 h-5 px-1.5 rounded-full border text-[10px] font-medium leading-none shrink-0 max-w-[80px] truncate transition-colors", isActive ? "bg-foreground text-background border-foreground" : "bg-subtle text-foreground border-border hover:border-foreground/40"].join(" ")}>
                  {color_0.name}
                </button>;
        })}
            {colors.length > 7 && <span className="text-[10px] text-muted-foreground ms-0.5">
                +{colors.length - 7}
              </span>}
          </div>}

        {/* Size chips */}
        {isVariable && sizes.length > 0 && <div className="flex items-center gap-1 mt-1.5 overflow-hidden">
            {sizes.slice(0, 5).map((size, i_0) => <span key={i_0} className="px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground bg-subtle rounded">
                {size}
              </span>)}
            {sizes.length > 5 && <span className="text-[10px] text-muted-foreground">
                +{sizes.length - 5}
              </span>}
          </div>}

        {/* mt-auto: the price is the one element that must line up across
            a row, whatever sits above it. */}
        <div className="mt-auto pt-2">
          {showUnitPricing && defaultUnit ?
        // Unit pricing headline: the per-unit price (e.g. per carton) leads,
        // with the base (per-piece) price underneath in the existing
        // small/muted treatment — same split ProductDetailClient uses.
        <div className="flex flex-col gap-0.5 mt-1.5">
              <span className="text-sm font-semibold text-foreground">
                <PriceLabel value={toUnitPrice(displayBase, defaultUnit.factor)} />
                {" / "}
                {locale === "ar" ? defaultUnit.name_ar : defaultUnit.name_en ?? defaultUnit.name_ar}
              </span>
              {product.base_unit && <span className="text-xs text-muted-foreground">
                  <PriceLabel value={displayBase} />
                  {" / "}
                  {locale === "ar" ? product.base_unit.name_ar : product.base_unit.name_en ?? product.base_unit.name_ar}
                </span>}
            </div> : <Price pricing={{
          base: displayBase,
          startingFrom,
          sale: product.sale_price != null && product.original_price != null ? {
            current: product.sale_price,
            original: product.original_price
          } : null
        }} locale={locale} size="sm" className="mt-1.5" />}
        </div>

        {/* Inline CTA — listing pages only. Sits below the price rather than
            over the photo, so the artwork stays unobstructed and the label
            says what the control does instead of relying on a bare glyph. */}
        {action === "inline" && <div className="mt-2.5">
            {isOutOfStock ? <SoldOutButton locale={locale} /> : isVariable ? <ChooseOptionsButton slug={product.slug} locale={locale} action="inline" /> : <CardAddToCartButton productId={product.id} price={product.price} minQty={cardMinQty} stock={product.track_stock && product.stock != null ? product.stock : null} locale={locale} action="inline" productUnitId={defaultUnit?.id ?? null} />}
          </div>}
      </div>
    </div>;
}